import { ReparoApi } from './index';
import { fetchPdfObjectUrl } from './pdf';
import {
    adaptOrder,
    type AdditionalCharge,
    type ProcurementOrder,
    type RawOrder,
} from './procurement-order';

// Frontend-facing "Order" (mother) shape. Shipments reuse the ProcurementOrder
// type (a shipment is a purchase_orders row) and the existing adaptOrder.

export type MotherOrderStatus =
    | 'DRAFT'
    | 'ISSUED'
    | 'IN_PRODUCTION'
    | 'PARTIALLY_SHIPPED'
    | 'SHIPPED'
    | 'AT_DESTINATION'
    | 'PARTIALLY_RECEIVED'
    | 'RECEIVED'
    | 'CANCELLED';

export type MotherOrderLine = {
    itemId: string;
    /** The row within the SKU; 'default' until a SKU is split by readiness. */
    lineItemId: string;
    /** When the line was written. An over-delivery line's is the receipt's moment. */
    createdAt: string | null;
    itemType: string;
    description: string | null;
    ordered: number;
    inProduction: number;
    inTransit: number;
    unitPrice: number | null;
    /** The line's GST slab. Null is "nobody stated one", never 0%. */
    gstRate: number | null;
    materialId: string | null;
    productId: string | null;
    /** The product's per-unit volume in CBM. Null = not on file, or a material. */
    volumeCbm: number | null;
    /** The brand's goods-ready date for this line (yyyy-MM-dd). Null = not stated. */
    expectedReadinessDate: string | null;
    /** QC as recorded on the line; both null = unjudged. */
    qcPassedQty: number | null;
    qcRejectedQty: number | null;
    /** BRAND = somebody recorded it (a ceiling); ASSUMED = written by a shipment. */
    qcSource: 'BRAND' | 'ASSUMED' | null;
    qcAt: string | null;
    /** Units already on a shipment of this order, whatever its dates. */
    shipped: number;
    /** Units of this line already received (GRN or marked received); 0 on a cancelled order. */
    received: number;
    /** Units a new shipment may still take: passed (or ordered) minus shipped. */
    shippable: number;
};

export type MotherOrder = {
    id: string;
    merchantId: string;
    orderNumber: string;
    poDate: string;
    expiryDate: string | null;
    expectedDeliveryDate: string | null;
    vendorId: string | null;
    vendorName: string | null;
    vendorEmail: string | null;
    vendorAddress: string | null;
    vendorGstin: string | null;
    billToName: string | null;
    billToAddress: string | null;
    billToGstin: string | null;
    shipToName: string | null;
    shipToAddress: string | null;
    shipToGstin: string | null;
    /** Place of supply, as a GST state code — '06'. Decides IGST vs CGST+SGST. */
    shipToStateCode: string | null;
    shipToEmail: string | null;
    contactName: string | null;
    contactEmail: string | null;
    currency: string;
    taxRate: number;
    subtotal: number;
    taxAmount: number;
    total: number;
    notes: string | null;
    termsAndConditions: string | null;
    additionalCharges: AdditionalCharge[];
    status: MotherOrderStatus;
    sentAt: string | null;
    inProductionAt: string | null;
    cancelledAt: string | null;
    orderedUnits: number;
    inProductionUnits: number;
    inTransitUnits: number;
    receivedUnits: number;
    /** Σ ordered × product CBM over product lines that carry one. Null when none do. */
    totalCbm: number | null;
    /** Product lines whose product has no CBM on file. */
    cbmMissingLines: number;
    /** The container cap to estimate against: the highest across the vendor's lanes. */
    maxCbmPerShipment: number | null;
    expectedReadinessDate: string | null;
    isSplit: boolean;
    destinationCountry: string | null;
    /** The WMS purchase order this order became (PO_AT_ORDER push policy). */
    wmsPoCode: string | null;
    wmsPushedAt: string | null;
    paymentSchedule: OrderPaymentSchedule | null;
    lines: MotherOrderLine[];
    shipments: ProcurementOrder[];
    /** Deliveries recorded at a site against this order, one per GRN. Single-order read only. */
    receipts: OrderReceipt[];
};

/** One delivery against an order: what came in, what was turned away, and whether it was undone. */
/**
 * Open the order as it would print if sent now - the vendor's document, rendered on request.
 * A blob the browser opens, as the production order's preview is: nothing is stored to link to.
 */
export const openMotherOrderPdf = async (orderId: string): Promise<void> => {
    const url = await fetchPdfObjectUrl(`/orders/${orderId}/preview.pdf`);
    window.open(url, '_blank');
};

/**
 * A line keyed `OVER-<GRN>` records goods a site took in above what the order asked for, on the
 * terms the brand chose at the gate. The id is the whole record - see the backend's
 * over-delivery-line util; these helpers read it the same way.
 */
export const OVER_DELIVERY_LINE_PREFIX = 'OVER-';

export const isOverDeliveryLine = (lineItemId: string): boolean =>
    lineItemId.startsWith(OVER_DELIVERY_LINE_PREFIX);

/** The over-delivery line a receipt added, if it added one. */
export const overDeliveryLineFor = (lines: MotherOrderLine[], grnId: string): MotherOrderLine | undefined =>
    lines.find((line) => line.lineItemId === `${OVER_DELIVERY_LINE_PREFIX}${grnId}`);

/** An over-delivery landed after the order was last sent, so the vendor holds an older copy. */
export const amendedSinceSent = (order: Pick<MotherOrder, 'sentAt' | 'lines'>): boolean =>
    !!order.sentAt &&
    order.lines.some(
        (line) => isOverDeliveryLine(line.lineItemId) && !!line.createdAt && line.createdAt > (order.sentAt as string),
    );

export type OrderReceipt = {
    grnId: string;
    purchaseOrderId: string;
    warehouseId: string;
    date: string;
    reference: string | null;
    enteredByName: string | null;
    itemIds: string[];
    received: number;
    rejected: number;
    reversed: boolean;
};

/**
 * What pushing to the WMS creates at each step under this merchant's push policy —
 * `GET /orders/wms-push-plan`. Nulls everywhere when there is no WMS or no policy.
 */
export type WmsPushPlan = {
    wmsType: string | null;
    mode: 'PO_AT_ORDER' | 'PO_PER_SHIPMENT' | null;
    order: 'CREATE_PO' | null;
    shipment: 'CREATE_PO' | 'CREATE_GRN' | null;
    receive: 'COMPLETE_GRN' | 'CREATE_GRN' | null;
    /** Production (batch) shipments: their own WMS PO, then a GRN at receipt. Null without a PO push policy. */
    standaloneShipment: 'CREATE_PO' | null;
    standaloneReceive: 'CREATE_GRN' | null;
    stn: 'CREATE_GATE_PASS' | null;
    distribution: 'CREATE_SALE_ORDER' | 'CREATE_GATE_PASS' | null;
};

export const WMS_STEP_LABEL: Record<
    NonNullable<WmsPushPlan['shipment'] | WmsPushPlan['receive']>,
    string
> = {
    CREATE_PO: 'create a purchase order in the WMS',
    CREATE_GRN: 'create a GRN in the WMS',
    COMPLETE_GRN: 'complete the GRN in the WMS',
};

export const getWmsPushPlan = (): Promise<WmsPushPlan> =>
    ReparoApi<WmsPushPlan>({ method: 'GET', url: '/orders/wms-push-plan' });

export const pushOrderToWms = async (orderId: string): Promise<MotherOrder> => {
    const raw = await ReparoApi<RawMotherOrder>({
        method: 'POST',
        url: `/orders/${orderId}/push-to-wms`,
        body: JSON.stringify({}),
    });
    return adaptMotherOrder(raw);
};

export const pushShipmentToWms = async (
    orderId: string,
    shipmentId: string,
): Promise<MotherOrder> => {
    const raw = await ReparoApi<RawMotherOrder>({
        method: 'POST',
        url: `/orders/${orderId}/shipments/${encodeURIComponent(shipmentId)}/push-to-wms`,
        body: JSON.stringify({}),
    });
    return adaptMotherOrder(raw);
};

/** A standalone (production) shipment becomes its own WMS purchase order. */
export const pushStandaloneShipmentToWms = async (poNumber: string): Promise<void> => {
    await ReparoApi<unknown>({
        method: 'POST',
        url: `/procurement-orders/${encodeURIComponent(poNumber)}/push-shipment-to-wms`,
        body: JSON.stringify({}),
    });
};

/** Mark a standalone (production) shipment received; with pushToWms, a GRN is raised against its WMS PO first. */
export const markStandaloneShipmentReceived = async (
    poNumber: string,
    options: { pushToWms?: boolean } = {},
): Promise<void> => {
    await ReparoApi<unknown>({
        method: 'POST',
        url: `/procurement-orders/${encodeURIComponent(poNumber)}/receive`,
        body: JSON.stringify({ pushToWms: options.pushToWms === true }),
    });
};

/** Raw schedule passthrough on the order (route/template/tranches). */
export type OrderPaymentSchedule = {
    route?: PaymentRoute;
    creditPartnerId?: string | null;
    creditPartner?: TranchePayee | null;
    template?: PaymentTrancheTemplate[];
    /** Which of the vendor's named terms the order was placed on; null on terms typed by hand. */
    termsOptionId?: string | null;
    termsName?: string | null;
    /** The rows were edited after the option was picked. */
    termsCustomised?: boolean;
    tranches: PaymentTranche[];
};

export type CreateMotherOrderItemInput = {
    /** The line's goods-ready date (yyyy-MM-dd). Optional. */
    expectedReadinessDate?: string | null;
    description: string;
    materialId?: string | null;
    productId?: string | null;
    quantity: number;
    unitPrice: number;
    /** The line's GST slab; null/omitted when nobody stated one. */
    gstRate?: number | null;
};

export type CreateMotherOrderInput = {
    /** Raised from a work order's deficit: the order's material lines are pegged to it on save. */
    workOrderId?: string;
    /** Destination warehouse - resolves the lane and lead times on the backend. */
    warehouseId?: string | null;
    orderNumber?: string | null;
    poDate?: string | null;
    expiryDate?: string | null;
    expectedDeliveryDate?: string | null;
    vendorId?: string | null;
    vendorName?: string | null;
    vendorEmail?: string | null;
    vendorAddress?: string | null;
    vendorGstin?: string | null;
    billToName?: string | null;
    billToAddress?: string | null;
    billToGstin?: string | null;
    shipToName?: string | null;
    shipToAddress?: string | null;
    shipToGstin?: string | null;
    /** Place of supply, as a GST state code — '06'. Decides IGST vs CGST+SGST. */
    shipToStateCode?: string | null;
    shipToEmail?: string | null;
    contactName?: string | null;
    contactEmail?: string | null;
    currency?: string;
    taxRate?: number;
    notes?: string | null;
    termsAndConditions?: string | null;
    items: CreateMotherOrderItemInput[];
    additionalCharges?: AdditionalCharge[];
    /** Terms-resolution key (country/market label, e.g. "India", "UAE"). */
    destinationCountry?: string | null;
    paymentTerms?: OrderPaymentTermsInput;
};

export type UpdateMotherOrderInput = Partial<
    Omit<CreateMotherOrderInput, 'orderNumber'>
>;

// ─── Payments (vendor-payments-v1) ──────────────────────────────

export type PaymentTrancheType = 'ADVANCE' | 'BALANCE' | 'CREDIT' | 'CUSTOM';
export type PaymentAnchor =
    | 'ORDER_DATE'
    | 'READINESS'
    | 'DISPATCH'
    | 'ARRIVAL'
    | 'DELIVERY'
    | 'FIXED';
export type PaymentRoute = 'DIRECT' | 'CREDIT_PARTNER';
export type PaymentTrancheStatus =
    | 'CANCELLED'
    | 'PAID'
    | 'PARTIALLY_PAID'
    | 'AWAITING_ANCHOR'
    | 'OVERDUE'
    | 'DUE_SOON'
    | 'DUE'
    | 'SCHEDULED';

export type PaymentTrancheTemplate = {
    label: string;
    type: PaymentTrancheType;
    percentage: number;
    anchor: PaymentAnchor;
    offsetDays?: number;
};

export type PaymentPayeeKind = 'VENDOR' | 'CREDIT_PARTNER' | 'FREIGHT_FORWARDER';

export type TranchePayee = {
    kind: PaymentPayeeKind;
    creditPartnerId?: string;
    /** FREIGHT_FORWARDER: the forwarder's vendor id. */
    vendorId?: string;
    name?: string;
    creditDays?: number;
    premiumPct?: number;
};

export type PaymentRecord = {
    id: string;
    amount: number;
    paidOn: string;
    method?: string | null;
    reference?: string | null;
    note?: string | null;
    recordedBy: string;
    recordedAt: string;
};

export type PaymentTranche = {
    id: string;
    label: string;
    type: PaymentTrancheType;
    percentage: number | null;
    amount: number;
    amountOverridden?: boolean;
    payee: TranchePayee;
    /** Absent = share of the goods value; FREIGHT = share of the freight charge. */
    basis?: 'GOODS_VALUE' | 'FREIGHT';
    anchor: PaymentAnchor;
    offsetDays?: number;
    dueDateOverride?: string | null;
    cancelledAt?: string | null;
    payments: PaymentRecord[];
};

export type DerivedTrancheState = {
    dueDate: string | null;
    dueDateSource: 'OVERRIDE' | 'ACTUAL' | 'EXPECTED' | null;
    status: PaymentTrancheStatus;
    paidAmount: number;
    outstanding: number;
};

export type OrderPaymentTermsInput = {
    route?: PaymentRoute;
    creditPartnerId?: string | null;
    template?: PaymentTrancheTemplate[];
    /** Which of the vendor's named terms was picked. */
    termsOptionId?: string | null;
    /** Keep these terms on the vendor, as a NEW option named `saveAsOptionName`. */
    saveToVendorDefault?: boolean;
    saveAsOptionName?: string | null;
    applyToExistingShipments?: boolean;
};

export type OrderTrancheView = {
    level: 'MOTHER' | 'SHIPMENT';
    shipmentPoId?: string;
    shipmentRef?: string;
    tranche: PaymentTranche;
    state: DerivedTrancheState;
    coveredByAdvance?: number;
};

export type OrderPaymentView = {
    route: PaymentRoute;
    creditPartnerName?: string | null;
    totals: {
        orderTotal: number;
        paid: number;
        dueToVendor: number;
        dueToCreditPartner: number;
        dueToForwarder: number;
        overdue: number;
        unscheduled: number;
    };
    advance: { percentage: number; paid: number; unallocated: number } | null;
    forecastResidual: {
        value: number;
        tranches: Array<{ label: string; amount: number }>;
    } | null;
    tranches: OrderTrancheView[];
};

export type PayableItem = {
    level: 'MOTHER' | 'SHIPMENT' | 'STANDALONE';
    orderId: string;
    orderRef: string;
    motherOrderId?: string;
    motherOrderNumber?: string;
    vendorId: string | null;
    vendorName: string | null;
    currency: string;
    trancheId: string;
    label: string;
    type: PaymentTrancheType;
    /** The terms behind the label. Percentage is null when the amount was set by hand. */
    percentage?: number | null;
    anchor?: PaymentAnchor;
    offsetDays?: number;
    creditDays?: number | null;
    premiumPct?: number | null;
    payeeKind: PaymentPayeeKind;
    payeeName: string | null;
    amount: number;
    paidAmount: number;
    outstanding: number;
    departureDate: string | null;
    expectedDepartureDate: string | null;
    dueDate: string | null;
    dueDateSource: 'OVERRIDE' | 'ACTUAL' | 'EXPECTED' | null;
    status: PaymentTrancheStatus;
};

export type PayablesView = {
    today: string;
    overdue: PayableItem[];
    unscheduled: PayableItem[];
    weeks: Array<{
        weekStart: string;
        items: PayableItem[];
        totalsByCurrency: Record<string, number>;
    }>;
    forecast: Array<{
        motherOrderId: string;
        orderRef: string;
        vendorId?: string | null;
        vendorName?: string | null;
        currency: string;
        residualValue: number;
        amount: number;
        tranches: Array<{ label: string; amount: number }>;
    }>;
    totals: {
        overdueByCurrency: Record<string, number>;
        dueThisWeekByCurrency: Record<string, number>;
        next30DaysByCurrency: Record<string, number>;
        unscheduledByCurrency: Record<string, number>;
    };
};

export type RecordPaymentInput = {
    amount: number;
    paidOn: string;
    method?: string | null;
    reference?: string | null;
    note?: string | null;
};

export type TrancheOverrideInput = {
    amount?: number | null;
    dueDateOverride?: string | null;
};

export type AddShipmentLineInput = {
    materialId?: string | null;
    productId?: string | null;
    quantity: number;
    /** The vendor's batch code for these units (packing list). Optional; a code repeated across SKUs is fine. */
    batchCode?: string | null;
    /** ISO date (yyyy-MM-dd). Stored on the batch, the only place expiry lives. */
    expiryDate?: string | null;
    manufacturingDate?: string | null;
};

export type AddShipmentInput = {
    name?: string | null;
    plannedDate?: string | null; // need-by → planned_date
    readinessDate: string; // GRD — mandatory; future OK (flips to in-transit when reached)
    dispatchDate?: string | null;
    arrivalDate?: string | null; // At Destination Port
    deliveryDate?: string | null; // delivered into the brand's warehouse (end state)
    warehouseId?: string | null; // destination → lane / lead times
    reference?: string | null;
    /** The CBM cap this shipment was measured against; recorded on the shipment. */
    maxCbmPerShipment?: number | null;
    lines: AddShipmentLineInput[];
    /** Also create the WMS document per push policy (GRN raised first under PO_AT_ORDER). */
    pushToWms?: boolean;
};

export type ShipmentMilestonesInput = {
    readinessDate?: string; // GRD — can be corrected, never cleared (no null)
    departureDate?: string | null;
    arrivalDate?: string | null; // At Destination Port
    deliveryDate?: string | null;
    warehouseId?: string | null; // destination → lane / lead times
};

/** One contract line's readiness and QC, as the brand records them. Omitted = untouched. */
export type UpdateOrderLineInput = {
    itemId: string;
    lineItemId?: string | null;
    expectedReadinessDate?: string | null;
    qcPassedQty?: number | null;
    qcRejectedQty?: number | null;
};

/** The dispatch booking on a shipment. Omitted = untouched, null = cleared. */
export type ShipmentFreightInput = {
    forwarderVendorId?: string | null;
    forwarderTermsOptionId?: string | null;
    freightAmount?: number | null;
    freightCurrency?: string | null;
    trackingRef?: string | null;
    /** Expected dispatch (the plan). */
    departureDate?: string | null;
};

export type ShipmentMilestone = 'DISPATCH' | 'ARRIVAL' | 'DELIVERY';

// ─── Adapters (wire -> frontend) ────────────────────────────────

type RawMotherLine = {
    itemId?: string;
    lineItemId?: string;
    createdAt?: string | null;
    itemType?: string;
    description?: string | null;
    quantity?: number | string;
    unitPrice?: number | string | null;
    gstRate?: number | string | null;
    inProductionQuantity?: number | string;
    inTransitQuantity?: number | string;
    volumeCbm?: number | string | null;
    expectedReadinessDate?: string | null;
    qcPassedQty?: number | string | null;
    qcRejectedQty?: number | string | null;
    qcSource?: 'BRAND' | 'ASSUMED' | null;
    qcAt?: string | null;
    shippedQuantity?: number | string;
    receivedQuantity?: number | string;
    shippableQuantity?: number | string;
};

type RawMotherOrder = {
    id?: string;
    merchantId?: string;
    orderNumber?: string;
    orderDate?: string | null;
    expiryDate?: string | null;
    expectedDeliveryDate?: string | null;
    vendorId?: string | null;
    vendorName?: string | null;
    vendorEmail?: string | null;
    vendorAddress?: string | null;
    vendorGstin?: string | null;
    billToName?: string | null;
    billToAddress?: string | null;
    billToGstin?: string | null;
    shipToName?: string | null;
    shipToAddress?: string | null;
    shipToGstin?: string | null;
    /** Place of supply, as a GST state code — '06'. Decides IGST vs CGST+SGST. */
    shipToStateCode?: string | null;
    shipToEmail?: string | null;
    contactName?: string | null;
    contactEmail?: string | null;
    currency?: string;
    taxRate?: number | string;
    subtotal?: number | string;
    taxAmount?: number | string;
    total?: number | string;
    notes?: string | null;
    termsAndConditions?: string | null;
    additionalCharges?: AdditionalCharge[];
    status?: string;
    sentAt?: string | null;
    inProductionAt?: string | null;
    cancelledAt?: string | null;
    orderedUnits?: number;
    inProductionUnits?: number;
    inTransitUnits?: number;
    receivedUnits?: number;
    totalCbm?: number | string | null;
    cbmMissingLines?: number;
    maxCbmPerShipment?: number | string | null;
    expectedReadinessDate?: string | null;
    isSplit?: boolean;
    destinationCountry?: string | null;
    wmsPoCode?: string | null;
    wmsPushedAt?: string | null;
    paymentSchedule?: OrderPaymentSchedule | null;
    lines?: RawMotherLine[];
    shipments?: RawOrder[];
    receipts?: OrderReceipt[];
};

function dateOnly(value: string | null | undefined): string {
    if (!value) return '';
    return String(value).slice(0, 10);
}

function adaptLine(raw: RawMotherLine): MotherOrderLine {
    const itemType = raw.itemType ?? 'product';
    const itemId = raw.itemId ?? '';
    return {
        itemId,
        lineItemId: raw.lineItemId ?? 'default',
        createdAt: raw.createdAt ?? null,
        itemType,
        description: raw.description ?? null,
        ordered: Number(raw.quantity ?? 0),
        inProduction: Number(raw.inProductionQuantity ?? 0),
        inTransit: Number(raw.inTransitQuantity ?? 0),
        unitPrice: raw.unitPrice == null ? null : Number(raw.unitPrice),
        gstRate: raw.gstRate == null ? null : Number(raw.gstRate),
        materialId: itemType === 'material' ? itemId : null,
        productId: itemType === 'material' ? null : itemId,
        volumeCbm: raw.volumeCbm == null ? null : Number(raw.volumeCbm),
        expectedReadinessDate: raw.expectedReadinessDate ? dateOnly(raw.expectedReadinessDate) : null,
        qcPassedQty: raw.qcPassedQty == null ? null : Number(raw.qcPassedQty),
        qcRejectedQty: raw.qcRejectedQty == null ? null : Number(raw.qcRejectedQty),
        qcSource: raw.qcSource ?? null,
        qcAt: raw.qcAt ?? null,
        shipped: Number(raw.shippedQuantity ?? 0),
        received: Number(raw.receivedQuantity ?? 0),
        shippable: Number(raw.shippableQuantity ?? 0),
    };
}

function adaptMotherOrder(raw: RawMotherOrder): MotherOrder {
    return {
        id: raw.id ?? '',
        merchantId: raw.merchantId ?? '',
        orderNumber: raw.orderNumber ?? '',
        poDate: dateOnly(raw.orderDate),
        expiryDate: raw.expiryDate ? dateOnly(raw.expiryDate) : null,
        expectedDeliveryDate: raw.expectedDeliveryDate
            ? dateOnly(raw.expectedDeliveryDate)
            : null,
        vendorId: raw.vendorId ?? null,
        vendorName: raw.vendorName ?? null,
        vendorEmail: raw.vendorEmail ?? null,
        vendorAddress: raw.vendorAddress ?? null,
        vendorGstin: raw.vendorGstin ?? null,
        billToName: raw.billToName ?? null,
        billToAddress: raw.billToAddress ?? null,
        billToGstin: raw.billToGstin ?? null,
        shipToName: raw.shipToName ?? null,
        shipToAddress: raw.shipToAddress ?? null,
        shipToGstin: raw.shipToGstin ?? null,
        shipToStateCode: raw.shipToStateCode ?? null,
        shipToEmail: raw.shipToEmail ?? null,
        contactName: raw.contactName ?? null,
        contactEmail: raw.contactEmail ?? null,
        currency: raw.currency ?? 'INR',
        taxRate: Number(raw.taxRate ?? 0),
        subtotal: Number(raw.subtotal ?? 0),
        taxAmount: Number(raw.taxAmount ?? 0),
        total: Number(raw.total ?? 0),
        notes: raw.notes ?? null,
        termsAndConditions: raw.termsAndConditions ?? null,
        additionalCharges: raw.additionalCharges ?? [],
        status: (raw.status as MotherOrderStatus) ?? 'DRAFT',
        sentAt: raw.sentAt ?? null,
        inProductionAt: raw.inProductionAt ?? null,
        cancelledAt: raw.cancelledAt ?? null,
        orderedUnits: Number(raw.orderedUnits ?? 0),
        inProductionUnits: Number(raw.inProductionUnits ?? 0),
        inTransitUnits: Number(raw.inTransitUnits ?? 0),
        receivedUnits: Number(raw.receivedUnits ?? 0),
        totalCbm: raw.totalCbm == null ? null : Number(raw.totalCbm),
        cbmMissingLines: Number(raw.cbmMissingLines ?? 0),
        maxCbmPerShipment: raw.maxCbmPerShipment == null ? null : Number(raw.maxCbmPerShipment),
        expectedReadinessDate: raw.expectedReadinessDate
            ? dateOnly(raw.expectedReadinessDate)
            : null,
        isSplit: !!raw.isSplit,
        destinationCountry: raw.destinationCountry ?? null,
        wmsPoCode: raw.wmsPoCode ?? null,
        wmsPushedAt: raw.wmsPushedAt ?? null,
        paymentSchedule: raw.paymentSchedule ?? null,
        lines: (raw.lines ?? []).map(adaptLine),
        shipments: (raw.shipments ?? []).map(adaptOrder),
        receipts: raw.receipts ?? [],
    };
}

function adaptCreatePayload(
    input: CreateMotherOrderInput,
): Record<string, unknown> {
    return { ...input };
}

// ─── API methods ────────────────────────────────────────────────

export const listMotherOrders = async (): Promise<MotherOrder[]> => {
    const raw = await ReparoApi<RawMotherOrder[]>({
        method: 'GET',
        url: '/orders',
    });
    return (raw ?? []).map(adaptMotherOrder);
};

export const getMotherOrder = async (id: string): Promise<MotherOrder> => {
    const raw = await ReparoApi<RawMotherOrder>({
        method: 'GET',
        url: `/orders/${id}`,
    });
    return adaptMotherOrder(raw);
};

export const createMotherOrder = async (
    payload: CreateMotherOrderInput,
): Promise<MotherOrder> => {
    const raw = await ReparoApi<RawMotherOrder>({
        method: 'POST',
        url: '/orders',
        body: JSON.stringify(adaptCreatePayload(payload)),
    });
    return adaptMotherOrder(raw);
};

export const updateMotherOrder = async (
    id: string,
    payload: UpdateMotherOrderInput,
): Promise<MotherOrder> => {
    const raw = await ReparoApi<RawMotherOrder>({
        method: 'PATCH',
        url: `/orders/${id}`,
        body: JSON.stringify(payload),
    });
    return adaptMotherOrder(raw);
};

export const addShipment = async (
    id: string,
    payload: AddShipmentInput,
): Promise<MotherOrder> => {
    const raw = await ReparoApi<RawMotherOrder>({
        method: 'POST',
        url: `/orders/${id}/shipments`,
        body: JSON.stringify(payload),
    });
    return adaptMotherOrder(raw);
};

/** Records readiness dates and QC verdicts on the order's contract lines. */
export const updateOrderLines = async (
    id: string,
    lines: UpdateOrderLineInput[],
): Promise<MotherOrder> => {
    const raw = await ReparoApi<RawMotherOrder>({
        method: 'PATCH',
        url: `/orders/${encodeURIComponent(id)}/lines`,
        body: JSON.stringify({ lines }),
    });
    return adaptMotherOrder(raw);
};

/** Books the forwarder / freight / tracking / expected dispatch on a shipment. */
export const updateShipmentFreight = async (
    orderId: string,
    shipmentId: string,
    input: ShipmentFreightInput,
): Promise<MotherOrder> => {
    const raw = await ReparoApi<RawMotherOrder>({
        method: 'PATCH',
        url: `/orders/${encodeURIComponent(orderId)}/shipments/${encodeURIComponent(shipmentId)}/freight`,
        body: JSON.stringify(input),
    });
    return adaptMotherOrder(raw);
};

/** Records that a milestone happened (or takes it back with `clear`). */
export const markShipmentMilestone = async (
    orderId: string,
    shipmentId: string,
    input: { milestone: ShipmentMilestone; on?: string | null; clear?: boolean },
): Promise<MotherOrder> => {
    const raw = await ReparoApi<RawMotherOrder>({
        method: 'POST',
        url: `/orders/${encodeURIComponent(orderId)}/shipments/${encodeURIComponent(shipmentId)}/mark-milestone`,
        body: JSON.stringify(input),
    });
    return adaptMotherOrder(raw);
};

export const updateOrderExpectedDates = async (
    orderId: string,
    patch: {
        expectedReadinessDate?: string | null;
        expectedDeliveryDate?: string | null;
    },
): Promise<MotherOrder> => {
    const raw = await ReparoApi<RawMotherOrder>({
        method: 'PATCH',
        url: `/orders/${orderId}/expected-dates`,
        body: JSON.stringify(patch),
    });
    return adaptMotherOrder(raw);
};

export const updateShipmentMilestones = async (
    orderId: string,
    shipmentId: string,
    milestones: ShipmentMilestonesInput,
): Promise<MotherOrder> => {
    const raw = await ReparoApi<RawMotherOrder>({
        method: 'POST',
        url: `/orders/${orderId}/shipments/${encodeURIComponent(shipmentId)}/milestones`,
        body: JSON.stringify(milestones),
    });
    return adaptMotherOrder(raw);
};

export const markShipmentReceived = async (
    orderId: string,
    shipmentId: string,
    options: { pushToWms?: boolean } = {},
): Promise<MotherOrder> => {
    const raw = await ReparoApi<RawMotherOrder>({
        method: 'POST',
        url: `/orders/${orderId}/shipments/${encodeURIComponent(shipmentId)}/receive`,
        body: JSON.stringify({ pushToWms: options.pushToWms === true }),
    });
    return adaptMotherOrder(raw);
};

export const unmarkShipmentReceived = async (
    orderId: string,
    shipmentId: string,
): Promise<MotherOrder> => {
    const raw = await ReparoApi<RawMotherOrder>({
        method: 'POST',
        url: `/orders/${orderId}/shipments/${encodeURIComponent(shipmentId)}/unreceive`,
        body: JSON.stringify({}),
    });
    return adaptMotherOrder(raw);
};

/**
 * Email the order to its supplier.
 *
 * TOOK NO ARGUMENTS UNTIL NOW, so there was no way to copy anyone on an order going out — the
 * screen was a bare "Sent to vendor" click. `to` is sent only when the sender changed it, and
 * overwrites the order's own address before the PDF renders so the document names where it
 * actually went.
 */
export const sendMotherOrder = async (
    id: string,
    options?: { cc?: string[]; to?: string[]; updateOrderAddress?: boolean },
): Promise<MotherOrder> => {
    const cc = (options?.cc ?? []).map((e) => e.trim()).filter(Boolean);
    const to = (options?.to ?? []).map((e) => e.trim()).filter(Boolean);
    const raw = await ReparoApi<RawMotherOrder>({
        method: 'POST',
        url: `/orders/${id}/send`,
        body: JSON.stringify({
            ...(cc.length > 0 ? { cc } : {}),
            ...(to.length > 0 ? { to } : {}),
            // Only when the sender ticked it. Absent leaves the order's recorded contact alone;
            // sending is a communication act, not an amendment to the order.
            ...(options?.updateOrderAddress ? { updateOrderAddress: true } : {}),
        }),
    });
    return adaptMotherOrder(raw);
};

export const cloneMotherOrder = async (id: string): Promise<MotherOrder> => {
    const raw = await ReparoApi<RawMotherOrder>({
        method: 'POST',
        url: `/orders/${id}/clone`,
        body: JSON.stringify({}),
    });
    return adaptMotherOrder(raw);
};

export const cancelMotherOrder = async (id: string): Promise<MotherOrder> => {
    const raw = await ReparoApi<RawMotherOrder>({
        method: 'POST',
        url: `/orders/${id}/cancel`,
        body: JSON.stringify({}),
    });
    return adaptMotherOrder(raw);
};

export const markMotherOrderIssued = async (
    id: string,
): Promise<MotherOrder> => {
    const raw = await ReparoApi<RawMotherOrder>({
        method: 'POST',
        url: `/orders/${id}/mark-issued`,
        body: JSON.stringify({}),
    });
    return adaptMotherOrder(raw);
};

export const markMotherOrderInProduction = async (
    id: string,
): Promise<MotherOrder> => {
    const raw = await ReparoApi<RawMotherOrder>({
        method: 'POST',
        url: `/orders/${id}/mark-in-production`,
        body: JSON.stringify({}),
    });
    return adaptMotherOrder(raw);
};

// ─── Payments API ───────────────────────────────────────────────

export const getOrderPayments = async (
    orderId: string,
): Promise<OrderPaymentView | null> => {
    return ReparoApi<OrderPaymentView | null>({
        method: 'GET',
        url: `/orders/${orderId}/payments`,
    });
};

/** A single PO's payment view — serves standalone synced POs (no mother). */
export const getShipmentPayments = async (
    poId: string,
): Promise<OrderPaymentView | null> => {
    return ReparoApi<OrderPaymentView | null>({
        method: 'GET',
        url: `/shipments/${encodeURIComponent(poId)}/payments`,
    });
};

export const updateOrderPaymentTerms = async (
    orderId: string,
    payload: OrderPaymentTermsInput,
): Promise<unknown> => {
    return ReparoApi<unknown>({
        method: 'PATCH',
        url: `/orders/${orderId}/payment-terms`,
        body: JSON.stringify(payload),
    });
};

export const getPayables = async (
    includeForecast = true,
): Promise<PayablesView> => {
    return ReparoApi<PayablesView>({
        method: 'GET',
        url: `/orders/payables?includeForecast=${includeForecast}`,
    });
};

/** level MOTHER → /orders/:id; level SHIPMENT/STANDALONE → /shipments/:poId */
const trancheBase = (
    level: PayableItem['level'] | OrderTrancheView['level'],
    orderId: string,
    trancheId: string,
): string =>
    level === 'MOTHER'
        ? `/orders/${orderId}/tranches/${trancheId}`
        : `/shipments/${encodeURIComponent(orderId)}/tranches/${trancheId}`;

export const recordTranchePayment = async (
    level: PayableItem['level'] | OrderTrancheView['level'],
    orderId: string,
    trancheId: string,
    payload: RecordPaymentInput,
): Promise<PaymentRecord> => {
    return ReparoApi<PaymentRecord>({
        method: 'POST',
        url: `${trancheBase(level, orderId, trancheId)}/payments`,
        body: JSON.stringify(payload),
    });
};

export const deleteTranchePayment = async (
    level: PayableItem['level'] | OrderTrancheView['level'],
    orderId: string,
    trancheId: string,
    paymentId: string,
): Promise<void> => {
    await ReparoApi<void>({
        method: 'DELETE',
        url: `${trancheBase(level, orderId, trancheId)}/payments/${paymentId}`,
    });
};

export const updateTrancheOverride = async (
    level: PayableItem['level'] | OrderTrancheView['level'],
    orderId: string,
    trancheId: string,
    payload: TrancheOverrideInput,
): Promise<PaymentTranche> => {
    return ReparoApi<PaymentTranche>({
        method: 'PATCH',
        url: trancheBase(level, orderId, trancheId),
        body: JSON.stringify(payload),
    });
};
